import { createSelector } from 'reselect';
import { compose, difference, get, keys } from 'lodash/fp';
import { createAction } from '@mews/framework';
import { omitFalsyValues } from '@mews/fp';
import { ApplicationState, Profile } from '@mews-commander/types';
import { EnterpriseProfilePrivileges } from '@mews-commander/legacy-data-structures';
import { mapValue, notEmpty, notNil } from '@mews-commander/utils';
import prefix from '../prefix';
import moduleSelector from '../selector';
import { mapPrivilegesFlagsToEnums } from '../utils';
import { createScopedEntitiesReducer, createScopedBaseEntitySelectors } from './utils';

export const NAME = 'enterpriseProfiles';

const ENTERPRISE_PROFILES_UPDATED = prefix('ENTERPRISE_PROFILES_UPDATED');
const ENTERPRISE_PROFILES_DELETED = prefix('ENTERPRISE_PROFILES_DELETED');

export const updateEnterpriseProfilesState = (entities: Profile[], scope: string) =>
    createAction(ENTERPRISE_PROFILES_UPDATED, entities, { scope });

export const enterpriseProfilesReducer = createScopedEntitiesReducer({
    UPDATE_SUCCESS: ENTERPRISE_PROFILES_UPDATED,
    DELETE_SUCCESS: ENTERPRISE_PROFILES_DELETED,
});

export const [getEnterpriseProfiles, getEnterpriseProfilesByIds, getEnterpriseProfileByOwnerId, getEnterpriseProfile] =
    createScopedBaseEntitySelectors<ApplicationState, Profile, typeof NAME, 'enterpriseId'>(
        moduleSelector,
        NAME,
        'enterpriseId'
    );

export const hasEnterpriseProfile = compose(notEmpty, getEnterpriseProfile);

export const getEnterpriseProfileName = createSelector(getEnterpriseProfile, get('name'));
export const getEnterpriseProfileData = compose(get('data'), getEnterpriseProfile);
export const getEnterpriseProfileValue = compose(get('value'), getEnterpriseProfileData);

export const isEnterpriseProfileAdmin = createSelector(getEnterpriseProfileValue, get('isAdmin'));
export const getEnterpriseProfileEmail = createSelector(getEnterpriseProfileValue, get('email'));

export const getEnterpriseProfileSuperiorProfileId = createSelector(
    getEnterpriseProfileValue,
    get('superiorProfileId')
);
export const hasEnterpriseProfileSuperiorProfileId = createSelector(getEnterpriseProfileSuperiorProfileId, notNil);

export const getEnterpriseProfilePrivileges = createSelector(getEnterpriseProfileValue, get('privileges'));

export const getEnterpriseProfileActivePrivilegeEnums = createSelector(
    getEnterpriseProfilePrivileges,
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    compose(mapPrivilegesFlagsToEnums, keys, omitFalsyValues)
);
export const getEnterpriseProfileActivePrivilegeValues = createSelector(
    getEnterpriseProfileActivePrivilegeEnums,
    mapValue
);

export const getEnterpriseProfileInactivePrivilegeEnums = createSelector(
    getEnterpriseProfileActivePrivilegeEnums,
    difference(EnterpriseProfilePrivileges.enumValues)
);

export const getEnterpriseProfileAssignedLearningPath = createSelector(
    getEnterpriseProfileValue,
    get('assignedLearningPath')
);
